import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { useStudioAuth } from './hooks/useStudioAuth';
import { useStudioProfileClaimed } from './hooks/useStudioProfileClaimed';
import './ClaimStudioProfile.css';

export default function ClaimStudioProfile({ studioName, onClaimSubmitted }) {
  const { user, claimStatus, loading } = useStudioAuth(studioName);
  const { isProfileClaimed, claimedBy } = useStudioProfileClaimed(studioName);
  
  // Core form state
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [storageAvailable, setStorageAvailable] = useState(null);
  
  // Document upload state
  const [documentFile, setDocumentFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  
  // Additional profile fields
  const [yearEstablished, setYearEstablished] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [tiktok, setTiktok] = useState('');
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);

  // Test storage availability on component mount
  useEffect(() => {
    const testStorageAccess = async () => {
      if (!user) return;
      
      try {
        console.log('🔍 Testing Firebase Storage availability...');
        console.log('📦 Storage config:', {
          bucket: storage.app.options.storageBucket,
          projectId: storage.app.options.projectId
        });
        
        // Create a minimal test file (1x1 pixel transparent PNG)
        const testImageData = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChAGAh+llhwAAAABJRU5ErkJggg==';
        const testBlob = new Blob([Uint8Array.from(atob(testImageData), c => c.charCodeAt(0))], { type: 'image/png' });
        // Use a path that's permitted by the security rules
        const testRef = ref(storage, `claims/${user.uid}/test_${Date.now()}.png`);
        
        try {
          const uploadTask = uploadBytesResumable(testRef, testBlob);
          
          uploadTask.on('state_changed', 
            (snapshot) => {
              console.log('✅ Storage test upload progress:', snapshot.bytesTransferred, '/', snapshot.totalBytes);
            },
            (error) => {
              console.error('❌ Storage test upload error:', error);
              setStorageAvailable(false);
              // Special message for permission errors
              if (error.code === 'storage/unauthorized') {
                setMessage(`❌ Storage permission denied. Please ensure you're signed in with the correct account.`);
              } else {
                setMessage(`❌ Storage test failed: ${error.message}. Please refresh the page and try again.`);
              }
            },
            async () => {
              try {
                console.log('✅ Storage test upload completed successfully');
                setStorageAvailable(true);
                setMessage('✅ Ready to submit profile claim');
              } catch (urlError) {
                console.error('❌ Error getting download URL:', urlError);
                setStorageAvailable(false);
                setMessage(`❌ Storage test failed: ${urlError.message}. Please refresh the page and try again.`);
              }
            }
          );
        } catch (taskError) {
          console.error('❌ Failed to create upload task:', taskError);
          setStorageAvailable(false);
          setMessage(`❌ Cannot create upload task: ${taskError.message}. Please refresh the page. If the issue persists, Storage may not be enabled.`);
        }
        
      } catch (error) {
        console.error('❌ Storage not available:', error);
        setStorageAvailable(false);
        setMessage('⚠️ Firebase Storage is not available. Please refresh the page and try again.');
      }
    };
    
    if (user) {
      testStorageAccess();
    }
  }, [user]);

  // Normalize website URL to include https:// if missing
  const normalizeWebsiteUrl = (url) => {
    if (!url) return '';
    
    let normalizedUrl = url.trim();
    
    // If URL doesn't start with http:// or https://, add https://
    if (!normalizedUrl.match(/^https?:\/\//)) {
      normalizedUrl = 'https://' + normalizedUrl;
    }
    
    return normalizedUrl;
  };

  // Helper function to normalize Facebook URL
  const normalizeFacebookUrl = (facebookValue) => {
    if (!facebookValue) return '';
    
    let normalized = facebookValue.trim();
    
    // Remove any existing facebook.com or www.facebook.com prefixes
    normalized = normalized
      .replace(/^(https?:\/\/)?(www\.)?facebook\.com\//, '')
      .replace(/^facebook\.com\//, '');
    
    // Return just the clean username/page name (not the full URL for storage)
    return normalized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setMessage('❌ You must be logged in to claim a studio profile');
      return;
    }

    if (isProfileClaimed) {
      setMessage('❌ This studio profile has already been claimed');
      return;
    }

    if (claimStatus === 'pending') {
      setMessage('⏳ You already have a pending claim for this studio profile');
      return;
    }

    if (claimStatus === 'approved') {
      setMessage('✅ Your claim for this studio profile has already been approved');
      return;
    }

    if (!documentFile) {
      setMessage('❌ Please upload a document for verification');
      return;
    }

    if (!documentType) {
      setMessage('❌ Please select a document type');
      return;
    }

    if (storageAvailable === false) {
      setMessage('❌ Storage is not available. Please refresh the page and try again.');
      return;
    }

    // Validate file sizes
    if (documentFile && documentFile.size > 10 * 1024 * 1024) {
      setMessage('❌ Document file exceeds maximum size of 10MB');
      return;
    }

    if (logoFile && logoFile.size > 5 * 1024 * 1024) {
      setMessage('❌ Logo file exceeds maximum size of 5MB');
      return;
    }

    // Validate logo is an image (not PDF)
    if (logoFile && !logoFile.type.startsWith('image/')) {
      setMessage('❌ Logo must be an image file (JPG, PNG)');
      return;
    }

    setSubmitting(true);
    setMessage('📤 Uploading files and submitting claim...');

    try {
      // Create organized folder structure for files
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // Format: 2024-01-15T14-30-45
      const sanitizedStudioName = studioName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      
      // Upload document file
      const documentExtension = documentFile.name.split('.').pop();
      const documentFileName = `${sanitizedStudioName}_${timestamp}.${documentExtension}`;
      // Use a path that's permitted by the security rules
      const documentRef = ref(storage, `claims/${user.uid}/${documentFileName}`);
      
      let documentURL = '';
      let logoURL = '';
      let logoFileName = '';
      
      // Upload document first
      const documentUploadTask = uploadBytesResumable(documentRef, documentFile);

      // Listen for document upload state change
      documentUploadTask.on('state_changed', 
        (snapshot) => {
          // Calculate progress percentage for document (weight: 70% of total)
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 70;
          setUploadProgress(progress);
          console.log(`📤 Document upload progress: ${progress.toFixed(1)}% - ${documentFileName}`);
        },
        (error) => {
          console.error('Document upload error:', error);
          if (error.code === 'storage/unauthorized') {
            setMessage(`❌ Permission denied. Please ensure you're signed in with the correct account.`);
          } else {
            setMessage(`❌ Document upload failed: ${error.message}`);
          }
          setSubmitting(false);
        },
        async () => {
          try {
            // Document uploaded successfully, get its URL
            documentURL = await getDownloadURL(documentUploadTask.snapshot.ref);
            console.log(`✅ Document uploaded successfully: ${documentFileName}`);
            
            // If logo file exists, upload it now
            if (logoFile) {
              const logoExtension = logoFile.name.split('.').pop();
              logoFileName = `${sanitizedStudioName}_studio_logo_${timestamp}.${logoExtension}`;
              // Use a path that's permitted by the security rules
              const logoRef = ref(storage, `claims/${user.uid}/${logoFileName}`);
              
              const logoUploadTask = uploadBytesResumable(logoRef, logoFile);
              
              // Listen for logo upload state change
              logoUploadTask.on('state_changed',
                (snapshot) => {
                  // Calculate progress percentage for logo (weight: 30% of total)
                  const documentProgress = 70; // Document is already complete
                  const logoProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 30;
                  setUploadProgress(documentProgress + logoProgress);
                  console.log(`📤 Logo upload progress: ${logoProgress.toFixed(1)}% - ${logoFileName}`);
                },
                (error) => {
                  console.error('Logo upload error:', error);
                  setMessage(`❌ Logo upload failed: ${error.message}, but document was uploaded.`);
                  submitClaimToFirestore(documentURL, '', ''); // Still submit claim without logo
                },
                async () => {
                  try {
                    // Logo uploaded successfully, get its URL
                    logoURL = await getDownloadURL(logoUploadTask.snapshot.ref);
                    console.log(`✅ Logo uploaded successfully: ${logoFileName}`);
                    
                    // Submit claim with both document and logo
                    submitClaimToFirestore(
                      documentURL, 
                      logoURL, 
                      `claims/${user.uid}/${logoFileName}`
                    );
                  } catch (urlError) {
                    console.error('Error getting logo download URL:', urlError);
                    submitClaimToFirestore(documentURL, '', ''); // Still submit claim without logo
                  }
                }
              );
            } else {
              // No logo file, submit claim with only document
              submitClaimToFirestore(documentURL, '', '');
            }
            
          } catch (urlError) {
            console.error('Error getting document download URL:', urlError);
            setMessage(`❌ Failed to get document URL: ${urlError.message}`);
            setSubmitting(false);
          }
        }
      );

    } catch (error) {
      console.error('Error in claim submission:', error);
      setMessage(`❌ Error: ${error.message}`);
      setSubmitting(false);
    }
  };

  // Helper function to submit claim to Firestore
  const submitClaimToFirestore = async (documentURL, logoURL, logoPath) => {
    try {
      // Normalize website URL if provided
      const normalizedWebsite = website ? normalizeWebsiteUrl(website) : '';
      
      // Process social media handles to remove @ if present
      const processedInstagram = instagram ? instagram.replace('@', '') : '';
      const processedFacebook = facebook ? normalizeFacebookUrl(facebook) : '';
      const processedTiktok = tiktok ? tiktok.replace('@', '') : '';
      
      // Prepare social media object
      const socialMedia = {
        instagram: processedInstagram,
        facebook: processedFacebook,
        tiktok: processedTiktok
      };
      
      // Create timestamp for submission
      const timestamp = Timestamp.now();
      
      // Prepare document path
      const sanitizedStudioName = studioName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      const documentPath = `claims/${user.uid}/${documentFile.name}`;
      
      // Submit claim to Firestore
      await addDoc(collection(db, 'studioProfileClaims'), {
        studioName,
        userId: user.uid,
        userEmail: user.email,
        documentType,
        documentURL,
        documentPath,
        documentFileName: documentFile.name,
        logoURL: logoURL || '',
        logoPath: logoPath || '',
        yearEstablished: yearEstablished || '',
        website: normalizedWebsite,
        description: description || '',
        socialMedia,
        status: 'pending',
        submittedAt: timestamp
      });

      setMessage('✅ Studio profile claim submitted successfully! We will review it soon.');
      
      // Reset form
      setDocumentFile(null);
      setDocumentType('');
      setLogoFile(null);
      setYearEstablished('');
      setWebsite('');
      setDescription('');
      setInstagram('');
      setFacebook('');
      setTiktok('');
      setUploadProgress(0);
      
      // Notify parent component
      if (onClaimSubmitted) {
        onClaimSubmitted();
      }
    } catch (firestoreError) {
      console.error('Error submitting claim:', firestoreError);
      setMessage(`❌ Failed to submit claim: ${firestoreError.message}`);
      setSubmitting(false);
    }
  };

  if (loading) return <div className="claim-studio-profile-loading">Loading...</div>;

  if (!user) {
    return (
      <div className="claim-studio-profile-container">
        <div className="claim-studio-profile-message">
          <h3>🔐 Login Required</h3>
          <p>You must be logged in to claim a studio profile.</p>
        </div>
      </div>
    );
  }

  if (isProfileClaimed) {
    return (
      <div className="claim-studio-profile-container">
        <div className="claim-studio-profile-message">
          <h3>✅ Studio Profile Already Claimed</h3>
          <p>This studio profile has been claimed by {claimedBy?.email || 'another user'}.</p>
        </div>
      </div>
    );
  }

  if (claimStatus === 'approved') {
    return (
      <div className="claim-studio-profile-container">
        <div className="claim-studio-profile-message success">
          <h3>🎉 Claim Approved!</h3>
          <p>Your claim for this studio profile has been approved. You can now edit the studio profile information.</p>
        </div>
      </div>
    );
  }

  if (claimStatus === 'pending') {
    return (
      <div className="claim-studio-profile-container">
        <div className="claim-studio-profile-message">
          <h3>⏳ Claim Pending Review</h3>
          <p>Your claim for this studio profile is currently under review. We'll notify you once it's processed.</p>
        </div>
      </div>
    );
  }

  if (claimStatus === 'rejected') {
    return (
      <div className="claim-studio-profile-container">
        <div className="claim-studio-profile-message error">
          <h3>❌ Claim Rejected</h3>
          <p>Your previous claim for this studio profile was rejected. Please contact support if you believe this was an error.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-studio-profile-container">
      <h3>🏢 Claim Studio Profile: {studioName}</h3>
      <p className="claim-studio-profile-intro">
        To claim this studio profile, please provide verification information. All claims are manually reviewed.
      </p>

      <form onSubmit={handleSubmit} className="claim-studio-profile-form">
        {/* Required Fields Section */}
        <div className="form-section">
          <h4>Required Information</h4>
          
          <div className="form-group">
            <label htmlFor="documentType">Verification Document Type (Owner) *</label>
            <select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              required
            >
              <option value="">Select document type</option>
              <option value="id">ID</option>
              <option value="passport">Passport</option>
              <option value="driving_licence">Driving Licence</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="documentFile">Upload Verification Document *</label>
            <input
              type="file"
              id="documentFile"
              onChange={(e) => setDocumentFile(e.target.files[0])}
              accept="image/*,.pdf"
              required
            />
            <small className="file-help">
              Accepted: Images (JPG, PNG) or PDF. Max 10MB.
            </small>
            {documentFile && (
              <div className="file-info">
                Selected: {documentFile.name} ({(documentFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>
        </div>
        
        {/* Optional Fields Section */}
        <div className="form-section">
          <h4>Optional Studio Information</h4>
          
          <div className="form-group">
            <label htmlFor="yearEstablished">Year Established</label>
            <input
              type="text"
              id="yearEstablished"
              value={yearEstablished}
              onChange={(e) => setYearEstablished(e.target.value)}
              placeholder="e.g., 2010"
            />
          </div>

          <div className="form-group">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="e.g., www.example.com"
            />
            <small className="input-help">
              URL will be automatically formatted (https:// added if missing).
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Studio Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us about your studio..."
              rows={4}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="logoFile">Studio Logo</label>
            <input
              type="file"
              id="logoFile"
              onChange={(e) => setLogoFile(e.target.files[0])}
              accept="image/*"
            />
            <small className="file-help">
              Accepted: Images (JPG, PNG). Max 5MB. Any shape or size.
            </small>
            {logoFile && (
              <div className="file-info">
                Selected: {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>
        </div>
        
        {/* Social Media Section */}
        <div className="form-section">
          <h4>Social Media Links (Optional)</h4>
          
          <div className="social-media-grid">
            <div className="form-group">
              <label htmlFor="instagram">Instagram</label>
              <input
                type="text"
                id="instagram"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="@username or URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="facebook">Facebook</label>
              <input
                type="text"
                id="facebook"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                placeholder="Facebook URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="tiktok">TikTok</label>
              <input
                type="text"
                id="tiktok"
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                placeholder="@username or URL"
              />
            </div>
          </div>
        </div>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span>{Math.round(uploadProgress)}% uploaded</span>
          </div>
        )}

        {message && (
          <div className={`claim-message ${
            message.includes('✅') ? 'success' : 
            message.includes('❌') ? 'error' : 
            message.includes('⏳') ? 'pending' : ''
          }`}>
            {message}
          </div>
        )}

        <button 
          type="submit" 
          disabled={submitting || storageAvailable === false}
          className="submit-claim-btn"
        >
          {submitting ? '📤 Submitting...' : '🏢 Submit Claim'}
        </button>
      </form>

      <div className="claim-profile-note">
        <p><strong>Note:</strong> Studio profile claims are manually reviewed to ensure authenticity. Please provide accurate information to speed up the review process.</p>
      </div>
    </div>
  );
}
